import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  Inventory2,
  Search,
  LocationOn,
  Visibility,
  Refresh,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { useShelfWiseInventory, useShelfWiseInventoryAll, useShelfInventoryById, SHELF_INVENTORY_SAFE_MAX } from '../../hooks/useWarehouseData';
import { useSearchDebounce } from '../../hooks/useDebounce';
import { ShelfInventoryItem } from '../../types';
import { statusAccent, compareShelf, ShelfSortKey, SortDir } from '../../utils/inventoryStatus';
import { formatDate } from '../../utils/formatDate';
import { eyebrow } from '../actionables/brand';
import StatPill from '../common/StatPill';
import ListRowCard from '../common/ListRowCard';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../common/TablePaginationBar';
import { useUrlParam, useSetUrlParams } from '../common/useUrlParam';

// Status accents (left strip + quantity text), the shelf sort keys, and the row comparator now come
// from utils/inventoryStatus (shared, unit-tested) — see the imports above. statusAccent returns the
// full accent set; only `main` is consumed here.

// Sortable columns. The `/shelfwise-inventory` endpoint (WarehouseAPI.getShelfWiseInventory) accepts
// only page/limit/skuCode — it has NO sort parameter. When the full result set is small enough
// (total <= SAFE_MAX) we fetch it in one request and sort across ALL pages (fullMode). Otherwise we
// fall back to the previous behaviour: client-side sort of the loaded page only, with a caption that
// discloses the page-only scope so there is no illusion of a dataset-wide sort while paginating.

// Shared column tracks for the list header row AND every data row, so all fields line up in fixed
// vertical columns. SKU grows to fill; the rest are fixed-width. Collapses to a stacked layout at xs.
const ROW_GRID = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr auto',
    md: 'minmax(180px, 2fr) 90px minmax(120px, 1fr) minmax(120px, 1fr) minmax(150px, 1fr) 56px',
  },
  alignItems: 'center',
  columnGap: 2,
} as const;

// Clickable column header for the card-list idiom: an `eyebrow`-styled label that toggles sort and
// shows a direction arrow when it is the active column. Mirrors the lightweight sort affordance used
// by the other tables, adapted to the ListRowCard header row (no MUI <Table>).
const SortHeader: React.FC<{
  label: string;
  column: ShelfSortKey;
  activeKey: ShelfSortKey;
  dir: SortDir;
  onSort: (key: ShelfSortKey) => void;
}> = ({ label, column, activeKey, dir, onSort }) => {
  const active = activeKey === column;
  const ariaSort: 'ascending' | 'descending' | 'none' = active
    ? (dir === 'asc' ? 'ascending' : 'descending')
    : 'none';
  return (
    <Box role="columnheader" aria-sort={ariaSort} sx={{ minWidth: 0 }}>
      <Box
        role="button"
        tabIndex={0}
        aria-label={`Sort by ${label}, ${active ? (dir === 'asc' ? 'ascending' : 'descending') : 'ascending'}`}
        onClick={() => onSort(column)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSort(column);
          }
        }}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.25, cursor: 'pointer',
          color: active ? '#DC5829' : '#8A8078', userSelect: 'none', borderRadius: '4px',
          '&:hover': { color: '#DC5829' },
          '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 2 },
        }}
      >
        <Typography sx={{ ...eyebrow, color: 'inherit', fontSize: '0.6rem' }}>{label}</Typography>
        {active && (dir === 'asc'
          ? <ArrowUpward sx={{ fontSize: 12 }} />
          : <ArrowDownward sx={{ fontSize: 12 }} />)}
      </Box>
    </Box>
  );
};

const ShelfInventoryViewer: React.FC = () => {
  const [selectedInventoryId, setSelectedInventoryId] = useState<string>('');
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const limit = DEFAULT_PAGE_SIZE;

  // Search/sort/page live in the URL (prefix "sh") so a refresh restores where the user was.
  // Defaults stay OUT of the URL; changing search/sort resets the page in the same update.
  const setUrlParams = useSetUrlParams();
  // The search box stays controlled on `skuCode`; the DEBOUNCED value is written to "shq"
  // (seeded from the URL on mount) so typing doesn't rewrite the URL on every keystroke.
  const [shq] = useUrlParam('shq', '');
  const [skuCode, setSkuCode] = useState(shq);
  const [sortKeyParam] = useUrlParam('shsort', 'skuCode');
  const [sortDirParam] = useUrlParam('shdir', 'asc');
  const sortKey = sortKeyParam as ShelfSortKey;
  const sortDir = sortDirParam as SortDir;
  const [pageParam, setPageParam] = useUrlParam('shpage', '0');
  const currentPage = parseInt(pageParam, 10) || 0;
  const setCurrentPage = (v: number) => setPageParam(v === 0 ? '0' : String(v));

  // Debounce SKU search input
  const { debouncedSearch: debouncedSkuCode, isDebouncing } = useSearchDebounce(skuCode, 500);

  // Push the debounced value into the URL (resetting the page in the same update). Seeding the
  // input from "shq" above makes an initial match a no-op write.
  React.useEffect(() => {
    if (debouncedSkuCode === shq) return;
    setUrlParams({ shq: debouncedSkuCode === '' ? null : debouncedSkuCode, shpage: null });
  }, [debouncedSkuCode, shq, setUrlParams]);

  // Probe query: the existing server-paged fetch. It renders the current page AND exposes
  // inventoryData.total, which decides whether a full-dataset sort is feasible.
  const {
    data: inventoryData,
    isLoading,
    error,
    refetch
  } = useShelfWiseInventory(debouncedSkuCode, currentPage, limit);

  // fullMode: the whole result set is small enough to pull in one request and sort across all pages.
  // Derived purely from the probe's total (available after the first page resolves). When the set is
  // larger than SAFE_MAX we stay in server-paged, page-scoped mode (the previous behaviour).
  const total = inventoryData?.total ?? 0;
  const fullMode = !!inventoryData && total > 0 && total <= SHELF_INVENTORY_SAFE_MAX;

  // Full-dataset fetch, gated on fullMode so it never fires for empty searches or oversized sets.
  const {
    data: inventoryDataAll,
    isLoading: isLoadingAll,
  } = useShelfWiseInventoryAll(debouncedSkuCode, total, fullMode);

  // Fetch individual inventory item details
  const {
    data: inventoryDetail,
    isLoading: isLoadingDetail,
    error: detailError
  } = useShelfInventoryById(selectedInventoryId);

  const handleSearch = (value: string) => {
    setSkuCode(value);
  };

  const handleViewDetails = (inventoryId: string) => {
    setSelectedInventoryId(inventoryId);
    setDetailDialogOpen(true);
  };

  // Toggle direction when re-clicking the active column, otherwise switch to the new column (asc).
  // Key + dir + the page reset all go through one URL update so they never clobber each other.
  const handleSort = (key: ShelfSortKey) => {
    let nextKey = sortKey;
    let nextDir: SortDir;
    if (key === sortKey) {
      nextDir = sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      nextKey = key;
      nextDir = 'asc';
    }
    setUrlParams({
      shsort: nextKey === 'skuCode' ? null : nextKey,
      shdir: nextDir === 'asc' ? null : nextDir,
      shpage: null,
    });
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedInventoryId('');
  };

  const hasResults = !!(inventoryData && debouncedSkuCode.length >= 2);

  // While fullMode is active, the full-dataset query may still be in flight; show the loading card
  // until it resolves so we don't flash a page-scoped ordering first.
  const awaitingFullData = fullMode && (isLoadingAll || !inventoryDataAll);

  // The rendered rows, sorted via the shared compareShelf comparator (copy before sorting so the
  // query cache's array is never mutated). In fullMode we sort the ENTIRE dataset, then slice the
  // current page — a true cross-page sort. Otherwise (total > SAFE_MAX) we sort only the loaded page,
  // preserving the previous server-paged, page-scoped behaviour.
  const sortedData = React.useMemo(() => {
    if (fullMode) {
      if (!inventoryDataAll?.data) return [];
      const all = [...inventoryDataAll.data].sort((a, b) => compareShelf(a, b, sortKey, sortDir));
      return all.slice(currentPage * limit, currentPage * limit + limit);
    }
    const rows = inventoryData?.data ? [...inventoryData.data] : [];
    rows.sort((a, b) => compareShelf(a, b, sortKey, sortDir));
    return rows;
  }, [fullMode, inventoryDataAll?.data, inventoryData?.data, sortKey, sortDir, currentPage, limit]);

  // Shared dashed-border empty/state card chrome (Inventory idiom).
  const stateCardSx = {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    textAlign: 'center' as const, py: 6, px: 3, borderRadius: '14px', bgcolor: 'background.paper',
    border: '1px dashed #ECECE4', mb: 2,
  };

  return (
    <Box>
      {/* Toolbar: pill search + Refresh */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Enter SKU code (e.g., dark, alpha, heart)…"
          value={skuCode}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ minWidth: 260, flex: '1 1 260px', '& .MuiOutlinedInput-root': { borderRadius: '999px', bgcolor: '#fff' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: '#8A8078' }} />
              </InputAdornment>
            ),
            endAdornment: isDebouncing && skuCode.length >= 2 && (
              <CircularProgress size={20} />
            ),
          }}
          helperText={
            isDebouncing && skuCode.length >= 2
              ? "Searching..."
              : inventoryData && debouncedSkuCode.length >= 2
              ? `Found ${inventoryData.total} inventory items`
              : "Enter at least 2 characters to search"
          }
        />
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => refetch()}
          disabled={isLoading}
          sx={{
            py: 1,
            px: 2.5,
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: '999px',
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Result-count stat pill — one meaningful metric (the server total), shown only once a
          search has resolved, mirroring Inventory's "Total" pill idiom. */}
      {hasResults && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
          <StatPill label="Results" value={inventoryData!.total} accent="#DC5829" />
        </Box>
      )}

      {/* Loading States */}
      {isDebouncing && skuCode.length >= 2 && (
        <Box sx={stateCardSx}>
          <CircularProgress size={24} sx={{ color: '#DC5829' }} />
          <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
            Searching inventory...
          </Typography>
        </Box>
      )}

      {isLoading && !isDebouncing && debouncedSkuCode.length >= 2 && (
        <Box sx={stateCardSx}>
          <CircularProgress sx={{ color: '#DC5829' }} />
          <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
            Loading inventory data...
          </Typography>
        </Box>
      )}

      {/* Full-dataset fetch in flight: the probe page resolved, but we hold the list until the whole
          set arrives so the first paint already reflects the cross-page sort (fullMode only). */}
      {!isLoading && !isDebouncing && !error && awaitingFullData && (
        <Box sx={stateCardSx}>
          <CircularProgress sx={{ color: '#DC5829' }} />
          <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
            Loading inventory data...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Box sx={stateCardSx}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
            Failed to load inventory data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            The inventory service may be unavailable. Please try again.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            sx={{ mt: 2, py: 1, px: 2.5, fontWeight: 700, textTransform: 'none', borderRadius: '999px' }}
          >
            Retry
          </Button>
        </Box>
      )}

      {/* Empty State — fewer than 2 chars typed */}
      {debouncedSkuCode.length > 0 && debouncedSkuCode.length < 2 && !isDebouncing && (
        <Box sx={stateCardSx}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(220,88,41,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
            <Search sx={{ color: '#DC5829' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Please enter at least 2 characters to search
          </Typography>
        </Box>
      )}

      {/* No Results */}
      {inventoryData && inventoryData.data.length === 0 && !isLoading && debouncedSkuCode.length >= 2 && (
        <Box sx={stateCardSx}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f4f1ea', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
            <Search sx={{ color: '#8A8078' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
            No inventory found for "{debouncedSkuCode}"
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Try searching with a different SKU code
          </Typography>
        </Box>
      )}

      {/* Results list — held back while the full-dataset fetch is still in flight (fullMode) so the
          first paint already reflects the cross-page sort. */}
      {inventoryData && inventoryData.data.length > 0 && !awaitingFullData && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Column header row — shares ROW_GRID with the data rows so labels sit exactly above
              their values. Each label is a clickable sort toggle with a direction arrow (the same
              lightweight affordance the other tables use). Hidden on xs where rows use the compact
              stacked layout. */}
          <Box
            sx={{
              ...ROW_GRID,
              display: { xs: 'none', md: 'grid' },
              px: 3, pb: 0.5,
            }}
          >
            <SortHeader label="SKU" column="skuCode" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
            <SortHeader label="Qty" column="quantity" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
            <SortHeader label="Shelf" column="shelf" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
            <SortHeader label="Warehouse" column="warehouse" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
            <SortHeader label="Last Updated" column="lastUpdated" activeKey={sortKey} dir={sortDir} onSort={handleSort} />
            <Box />
          </Box>

          {/* Scope disclosure — only in the server-paged fallback (!fullMode, total > SAFE_MAX),
              where sorting reorders just the page in view. In fullMode the whole dataset is sorted,
              so this caption is hidden. */}
          {!fullMode && (
            <Typography variant="caption" sx={{ color: '#8A8078', px: 3, pb: 0.5 }}>
              <Box component="span" sx={{ fontWeight: 600 }}>Sorting applies to this page only</Box>
              {' — the inventory service returns results one page at a time.'}
            </Typography>
          )}

          {sortedData.map((item: ShelfInventoryItem, index: number) => {
            const accent = statusAccent(item.quantity);
            return (
              <ListRowCard key={item.id} accent={accent.main} index={index}>
                <Box sx={{ ...ROW_GRID, pl: 3, pr: 2, py: 1.25, rowGap: 0.5 }}>
                  {/* SKU code + name (shelf location shows once, in its dedicated column below) */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }} noWrap>
                      {item.skuCode}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                      {item.skuName || 'N/A'}
                    </Typography>
                  </Box>

                  {/* Quantity */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800, color: accent.main, lineHeight: 1.2,
                      justifySelf: { xs: 'end', md: 'start' }, gridColumn: { xs: '2', md: 'auto' },
                    }}
                  >
                    {item.quantity}
                  </Typography>

                  {/* Shelf location (dedicated column) */}
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', display: { xs: 'none', md: 'block' } }}
                    noWrap
                  >
                    {item.shelfLocation}
                  </Typography>

                  {/* Warehouse location */}
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', display: { xs: 'none', md: 'block' } }}
                    noWrap
                  >
                    {item.warehouseLocation || 'N/A'}
                  </Typography>

                  {/* Last updated */}
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', display: { xs: 'none', md: 'block' } }}
                    noWrap
                  >
                    {formatDate(item.lastUpdated || item.updatedAt)}
                  </Typography>

                  {/* Actions */}
                  <Box sx={{ justifySelf: 'end' }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        aria-label="View details"
                        onClick={() => handleViewDetails(item.id)}
                        sx={{ color: '#DC5829', bgcolor: 'rgba(220,88,41,0.08)', '&:hover': { bgcolor: 'rgba(220,88,41,0.16)' } }}
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </ListRowCard>
            );
          })}

          {/* Pagination — total is always the server's full count. In fullMode the page slice comes
              from the locally-sorted full set; otherwise currentPage drives the server fetch. Either
              way currentPage is 0-indexed and setCurrentPage is the setter both queries read. */}
          <TablePaginationBar
            page={currentPage}
            total={inventoryData.total}
            rowsPerPage={limit}
            onPageChange={setCurrentPage}
          />
        </Box>
      )}

      {/* Inventory Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Inventory2 />
            Inventory Item Details
          </Box>
        </DialogTitle>

        <DialogContent>
          {isLoadingDetail && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#DC5829' }} />
              <Typography sx={{ ml: 2 }}>Loading details...</Typography>
            </Box>
          )}

          {detailError && (
            <Box sx={stateCardSx}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                Failed to load inventory details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Please try again.
              </Typography>
            </Box>
          )}

          {inventoryDetail && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                <Box sx={{ minWidth: '200px', flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Inventory ID
                  </Typography>
                  <Typography variant="body1">
                    {inventoryDetail.id}
                  </Typography>
                </Box>

                <Box sx={{ minWidth: '200px', flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    SKU Code
                  </Typography>
                  <Typography variant="body1">
                    {inventoryDetail.skuCode}
                  </Typography>
                </Box>

                <Box sx={{ minWidth: '200px', flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    SKU Name
                  </Typography>
                  <Typography variant="body1">
                    {inventoryDetail.skuName || 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ minWidth: '200px', flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Quantity
                  </Typography>
                  <Chip
                    label={inventoryDetail.quantity}
                    color={inventoryDetail.quantity > 0 ? 'success' : 'error'}
                    variant="outlined"
                  />
                </Box>

                <Box sx={{ minWidth: '200px', flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Shelf Location
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body1">
                      {inventoryDetail.shelfLocation}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ minWidth: '200px', flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Warehouse Location
                  </Typography>
                  <Typography variant="body1">
                    {inventoryDetail.warehouseLocation || 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ minWidth: '200px', flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(inventoryDetail.createdAt)}
                  </Typography>
                </Box>

                <Box sx={{ minWidth: '200px', flex: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(inventoryDetail.lastUpdated || inventoryDetail.updatedAt)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseDetail}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShelfInventoryViewer;
